import React, { FunctionComponent } from 'react'
import { ActionCard, Radio, DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import { useCountry, useInternationalShippingEnabled, useInternationalSupportedShippingCountries, useShippingAddress } from '../../state'
import { ShippingAddressItem } from '..'
import { ShippingAddress } from '../../model'
import { isUndefinedOrEmpty } from '@afterpay/utils/string'
import { valueIsNotNullOrUndefined } from '../../utils/validation'
import { isValidAddress } from '../../utils/profile-validation'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { useTranslation } from '../../utils/convergence'

interface ShippingAddressListProps {
  onChange: (address: ShippingAddress | undefined) => void
  defaultShippingAddress?: ShippingAddress
  addNewAddressClick: () => void
  addNewAddressClickDisabled: boolean
}

export const ShippingAddressList: FunctionComponent<ShippingAddressListProps> = (props) => {
  const { t } = useTranslation()
  const { defaultShippingAddress, onChange, addNewAddressClick, addNewAddressClickDisabled } = props
  const { allAddresses } = useShippingAddress()
  const supportedShippingCountries = useInternationalSupportedShippingCountries()
  const isInternationalShippingEnabled = useInternationalShippingEnabled()
  const consumerCountry = useCountry()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const isRadioDisabled = (address: ShippingAddress): boolean => {
    const isRadioDisabled = isUndefinedOrEmpty(address?.phoneNumber) || !isValidAddress(address)
    const isAddressSupported = supportedShippingCountries.includes(address.countryCode)

    if (isInternationalShippingEnabled) {
      return isRadioDisabled || !isAddressSupported
    }
    if (address.countryCode !== consumerCountry) {
      return true
    }
    return isRadioDisabled
  }

  const sortedAddresses: ShippingAddress[] = allAddresses.slice().sort((a, b) => {
    // sort by supported country
    const sortInternationalSupported = Number(supportedShippingCountries.includes(b.countryCode)) - Number(supportedShippingCountries.includes(a.countryCode))
    const sortStandard = Number(b.countryCode === consumerCountry) - Number(a.countryCode === consumerCountry)

    const sortByCountryCode = isInternationalShippingEnabled ? sortInternationalSupported : sortStandard

    // sort by preferred
    const sortByPreferred = Number(b.preferred) - Number(a.preferred)

    // sort by enabled status
    const sortByEnabled = Number(isRadioDisabled(a)) - Number(isRadioDisabled(b))

    // sort by phone number defined
    const itemAHasPhoneNumber = !valueIsNotNullOrUndefined(a.phoneNumber) ? 0 : 1
    const itemBHasPhoneNumber = !valueIsNotNullOrUndefined(b.phoneNumber) ? 0 : 2
    const sortPhoneExists = itemBHasPhoneNumber - itemAHasPhoneNumber
    return sortByCountryCode || sortByEnabled || sortByPreferred || sortPhoneExists
  })

  const labelPlacement = isArcadeDesignLanguage ? 'Top' : 'Middle'
  const actionCardPosition = isBronteDesignLanguage && sortedAddresses.length > 0 ? 'Bottom' : 'Default'

  return (
    <MaskedElement actionName='Select Shipping Address'>
      <Radio.Group
        indent
        labelPlacement='Middle'
        labelWidth='Full'
        name='shipping-address'
        defaultValue={defaultShippingAddress?.id.toString()}
        onChange={(e) => onChange(sortedAddresses.find((address) => address.id.toString() === e.target.value))}
        kind='Option'
      >
        {sortedAddresses.map((address, idx) => {
          const showIncompleteAddress = isUndefinedOrEmpty(address?.phoneNumber) || !isValidAddress(address)
          const isDisabled = isRadioDisabled(address)
          return [
            <Radio.Button
              key={address.id}
              value={address.id.toString()}
              id={`shipping-address-${address.id}`}
              kind='Option'
              testNameSpace={`shipping-address-${address.id}`}
              disabled={isDisabled}
              label={() => <ShippingAddressItem {...address} disabled={isDisabled} showIncompleteAddress={showIncompleteAddress} />}
              labelPlacement={labelPlacement}
              roundedBorder={isArcadeDesignLanguage}
              reversed={isArcadeDesignLanguage}
            />,
          ]
        })}
        <Radio.Footer sticky>
          <ActionCard
            label={t('shippingAddress:addNewShippingAddress')}
            iconName='Add'
            onClick={addNewAddressClick}
            position={actionCardPosition}
            disabled={addNewAddressClickDisabled}
            testNameSpace='add-new-shipping-address'
          />
        </Radio.Footer>
      </Radio.Group>
    </MaskedElement>
  )
}
